import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { ThemeToggle } from '@/components/ThemeToggle';
import { api, ApiError, ensureCsrf } from '@/api/client';
import { useToast } from '@/components/Toast';
import type { MessageResponse } from '@/api/types';

export function Register() {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  const register = useMutation({
    mutationFn: async () => {
      await ensureCsrf();
      return api<MessageResponse>('/auth/register', { method: 'POST', body: { email, password } });
    },
    onSuccess: () => setDone(true),
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        toast.push({ tone: 'danger', title: 'Could not register', description: err.message });
      }
    },
  });

  if (done) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-card__brand">
            <span className="auth-card__brand-mark" aria-hidden />
            <span>Polymarket Surveillance</span>
            <div className="spacer" />
            <ThemeToggle />
          </div>
          <h2 className="auth-card__title">Check your email</h2>
          <p className="auth-card__subtitle">
            If your address is on the allow-list, a verification link is on its way. The link
            expires in 30 minutes.
          </p>
          <Link to="/login">
            <Button variant="primary" block>
              Back to sign in
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card__brand">
          <span className="auth-card__brand-mark" aria-hidden />
          <span>Polymarket Surveillance</span>
          <div className="spacer" />
          <ThemeToggle />
        </div>
        <h2 className="auth-card__title">Request access</h2>
        <p className="auth-card__subtitle">
          Registration is limited to approved domains. We'll email a verification link.
        </p>
        <form
          className="stack-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (password.length < 8) {
              toast.push({
                tone: 'warning',
                title: 'Password too short',
                description: 'Use at least 8 characters.',
              });
              return;
            }
            if (password !== confirm) {
              toast.push({
                tone: 'warning',
                title: 'Passwords differ',
                description: 'Make sure both password fields match.',
              });
              return;
            }
            register.mutate();
          }}
        >
          <Input
            label="Institutional email"
            type="email"
            name="email"
            autoComplete="email"
            required
            placeholder="you@upenn.edu"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Password"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            hint="At least 8 characters; mix letters, numbers, and symbols."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            label="Confirm password"
            type="password"
            name="password2"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <Button variant="primary" type="submit" block disabled={register.isPending}>
            {register.isPending ? 'Creating…' : 'Create account'}
          </Button>
        </form>
        <div className="auth-card__footer">
          Already registered? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
